import { type Objectish } from "immer";
import { Subject } from "rxjs";
import { type Subscription } from "xstate";

import { type CanvasSpecifier } from "../../api-types";
import { type DoTransactionFn } from "../../metadata-state";
import { observableToAsyncGenerator } from "../../observableToAsyncGenerator";
import { type PageStorageBridge } from "../../page-local-storage/client/PageStorageBridge";
import { type PageStorageFactory } from "../../page-local-storage/client/index";
import { type PageLocalStorageClient } from "../../page-local-storage/client/pageLocalStorageClient";

/**
 * A `PageStorageBridge` between the canvas runtime and the interactive, optimistically-committing
 * K/V store that runs in Cloudflare.
 */
export class CollaborativePageStorageBridge implements PageStorageBridge {
	private storageFactory: PageStorageFactory;
	private _lastData?: Objectish;
	private _dataUpdates = new Subject<Objectish>();
	private _clientSubscription?: Subscription;
	private _client?: PageLocalStorageClient;
	public pageInfo?: CanvasSpecifier;
	private disposables: (() => void)[] = [];

	constructor(
		pageInfo: CanvasSpecifier,
		storageFactory: PageStorageFactory,
		client?: PageLocalStorageClient
	) {
		this.storageFactory = storageFactory;
		this.init(pageInfo, client);
	}

	private init(pageInfo: CanvasSpecifier, _client?: PageLocalStorageClient) {
		this.pageInfo = pageInfo;
		this._client = _client;
		this._clientSubscription = this._client?.subscribe((data) => {
			this._lastData = data;
			this._dataUpdates.next(data);
		});
	}

	async storageFor(canvasId: string, pageId: string): Promise<PageStorageBridge> {
		const canvasSpecifier = {
			id: canvasId,
			pageId,
		};

		if (this.pageInfo?.id === canvasId && this.pageInfo?.pageId === pageId) {
			return this;
		}

		const { client: newStorageClient, onDispose } = this.storageFactory.create(
			canvasId,
			pageId
		);
		const newBridge = new CollaborativePageStorageBridge(
			canvasSpecifier,
			this.storageFactory,
			newStorageClient
		);
		await newStorageClient.idempotentInitRoom();
		this.disposables.push(onDispose);
		return newBridge;
	}

	get data(): AsyncIterableIterator<Objectish> {
		async function* lastResultThenValues(pageStorageBridge: CollaborativePageStorageBridge) {
			//
			// [MOM-7169] PREVENT CRITICAL RACES! Read VERY CAREFULLY!
			//
			// tl;dr it is *VERY* easy for `_lastData` to become a stale reference. This causes
			// canvases to behave EXTREMELY strangely: they hang, they report incorrect data in page
			// storage, writes to page storage do not work, and so on.
			//
			// This comment will describe common signs that this has happened, WHY this has happened
			// in the past, specific examples of when it happened, why the current (2024/06/19)
			// implementation addresses them, what alternatives DO NOT WORK, and who to contact if
			// things seem like they might have gone wrong.
			//
			//   1. SIGNS WE MIGHT HAVE MESSED THIS METHOD UP:
			//
			//      - Sometimes `pageStorage` in a canvas returns `undefined`.
			//      - Sometimes `pageStorage` returns data that seems like it's from another canvas.
			//      - Sometiems `pageStorage` returns an empty object `{}`.
			//
			//   2. WHY THIS HAPPENS.
			//
			//      - `_lastData` gets stale. That is, it refers to some previous value. Often this
			//        value is `undefined`.
			//      - This is because the runtime calls `CollaborativePageStorageBridge#data` to
			//        create an iterator IMMEDIATELY when it starts up (when `_lastData` is usually
			//        `undefined`), but it doesn't consume the iterator until later (when
			//        `_lastData` is usually no longer `undefined`).
			//      - That is: when we start iterating values, we usually expect `_lastData` to be
			//        up-to-date, but because the iterator was created, `_lastData` was `undefined`!
			//
			//   3. EXAMPLE OF IT HAPPENING.
			//
			//      - We open a new canvas.
			//      - We connect to a new page storage server.
			//      - This takes a second or two the first time.
			//      - The runtime starts up and calls `data` to create an iterator.
			//      - Now a cell wants to consume this data. It starts iterating the iterator.
			//      - Because the runtime created the iterator when `_lastData` was `undefined`, it
			//        sees the page storage as `undefined`.
			//
			//   4. WHY THE CURRENT (2024/06/19) IMPLEMENTATION FIXES IT.
			//
			//      - The problem is that we need the iterator to begin with the value of
			//        `_lastData` *AT THE TIME WE START ITERATING*.
			//      - It would be a BUG to begin with the value of `_lastData` at the time the
			//        iterator was CREATED (here it will often be `undefined`).
			//      - `yield pageStorageBridge._lastData` below causes the iterator to begin with
			//        the CURRENT VALUE of `_lastData`, no matter when
			//        `CollaborativePageStorageBridge#data` was called (we would say this is the
			//        moment "the iterator is created").
			//
			//   5. THINGS THAT DO NOT WORK.
			//
			//      - Using RxJS `startWith` DOES NOT WORK.
			//      - That is, this does not work:
			//        `observableToAsyncGenerator(pageStorageBridge._dataUpdates.pipe(startWith(this._lastData)))`
			//      - This would cause the iterator to begin with with the value of `_lastData`
			//        WHENEVER THE ITERATOR WAS CREATED.
			//      - This is NOT what we want. We want to start with the value of `_lastData` at
			//        the point we START ITERATING.
			//
			//   6. WHO TO FIND IF THIS SEEMS BROKEN.
			//
			//      - Probably Alex.
			//      - Alex debugged it and was on Rx core back before RxJS was a thing and can give
			//        you a more detailed explanation of how this went wrong.
			//

			if (pageStorageBridge._lastData !== undefined) {
				// DO NOT MOVE THIS LINE OUT OF `data` UNLESS YOU KNOW WHAT YOU ARE DOING. See
				// comment above or talk to Alex if you have questions.
				yield pageStorageBridge._lastData;
			}

			yield* observableToAsyncGenerator(pageStorageBridge._dataUpdates);
		}

		return lastResultThenValues(this);
	}

	commit = async (_doTransaction: DoTransactionFn<Objectish>): Promise<void> => {
		await this._client?.commit();
	};

	replaceClient(pageInfo: CanvasSpecifier, _client?: PageLocalStorageClient) {
		this._clientSubscription?.unsubscribe();
		this.init(pageInfo, _client);
	}

	dispose() {
		//
		// IMPORTANT: Do NOT call `.dispose` on `this._client` itself! THE BRIDGE DOES NOT MANAGE
		// THE LIFECYCLE OF THE CLIENT! All it does is manage the resources to BRIDGE the client and
		// the canvas runtime!
		//

		this._dataUpdates.complete();
		this._clientSubscription?.unsubscribe();
		this.disposables.forEach((fn) => fn());
		this.disposables = [];
	}
}
